











































































import { Vue, Prop, Component, Watch } from 'vue-property-decorator';
import {
    CreateFavoriteListRequest,
    FavoriteListPermission,
    FavoriteListViewObject,
    FavoritesItemsListedViewObject,
    LineItemViewObject
} from '@/types/apiServerContract';

import myAccountStore from '@/project/app/client-pages/my-account/my-account.store';

import ProductFavoriteItemsService from '@/project/product/services/productFavoriteItems.service';
import loggingService from '@/core/logging.service';
import dictionaryService from '@/core/translation/dictionary.service';
import FavoriteListCrudOverlayService from '@/project/modals/overlays/FavoriteListCrudOverlay.service';
import loginFormOverlayService from '@/project/modals/overlays/LoginFormOverlay.service';

import SpinnerOverlay from '@/project/spinners/SpinnerOverlay.vue';
import translateFilter from '@/core/translation/translate.filter';

interface FavoriteListViewObjectExtended extends FavoriteListViewObject {
    checked: boolean;
    loading: boolean;
}

@Component({
    components: { SpinnerOverlay }
})
export default class BasketFavorite extends Vue {
    @Prop({ required: true, type: Array })
    private lineItems!: LineItemViewObject[];

    @Prop({ required: true, type: Boolean })
    private isLoggedIn!: boolean;

    @Prop()
    private overlayClass!: string;

    @Prop({ type: String, default: 'top-0 z-2' })
    private overlayInnerClass!: string;

    @Prop({ type: String, default: translateFilter('Basket.Actions.AddToFavorites') })
    private callToActionText!: string;

    @Prop({ type: String })
    private callToActionClass!: string;

    private loadingLists: boolean = false;
    private showOverlay: boolean = false;
    private showCreateFavouriteListOverlay: boolean = false;

    private listInformation: FavoritesItemsListedViewObject[] = [];

    async created() {
        await this.checkIfLineItemsAreListed();
    }

    get favoriteLists(): FavoriteListViewObject[] {
        return myAccountStore.allFavouriteLists.map(x => ({
            ...x,
            checked: this.areAllLineItemsIn(x)
        }) as FavoriteListViewObjectExtended)
            .filter(x => this.canEditList(x) || this.areAllLineItemsIn(x));
    }

    get icon(): string {
        return this.listInformation.length > 0 ? 'favorite' : 'favorite-outline';
    }

    @Watch('isLoggedIn')
    async onLoginStateChange(state: boolean) {
        if (state) {
            await this.checkIfLineItemsAreListed();
        }
    }

    handleClickShowOverlay() {
        this.showOverlay = true;
        this.$emit('showOverlay', true);
    }

    private areAllLineItemsIn({ id }: FavoriteListViewObject): boolean {
        return this.listInformation.reduce((allInList, { listIds }) => {
            if (!listIds.some(lId => lId === id)) {
                allInList = false;
            }

            return allInList;
        }, true as boolean); // Typescript is fun
    }

    canEditList(list: FavoriteListViewObject): boolean {
        return list.permission === FavoriteListPermission.Edit;
    }

    private async checkIfLineItemsAreListed() {
        if (!this.isLoggedIn || this.loadingLists) {
            return;
        }

        this.loadingLists = true;

        try {
            const results = await Promise.allSettled(this.lineItems.map(item => ProductFavoriteItemsService.requestData(item.itemId).promise));

            if (results && results.length) {
                this.listInformation = results.map(({ status, value }: any, index) => {
                    if (status === 'rejected') {
                        return { itemId: this.lineItems[index]?.itemId, listIds: [] } as FavoritesItemsListedViewObject;
                    }

                    return value as FavoritesItemsListedViewObject;
                });
            }
        } catch (e) {
            console.error(e);
            // No need to log any potential errors here since it's dealt with in our "AbstractBulkFetch" method.
        } finally {
            this.loadingLists = false;
        }
    }

    titleForList(list: FavoriteListViewObject): string | null {
        if (this.canEditList(list)) {
            return null;
        }

        return dictionaryService.get('FavoriteList.Products.NoPermission');
    }

    private async createList() {
        if (!this.isLoggedIn) {
            return;
        }

        this.showCreateFavouriteListOverlay = true;

        try {
            await FavoriteListCrudOverlayService.show(null, false, 'create');
        } finally {
            this.showCreateFavouriteListOverlay = false;
        }
    }

    closeOverlay(): void {
        // Ensure that we don't trigger the outside click when he create modal is open
        if (this.showCreateFavouriteListOverlay) {
            return;
        }

        this.showOverlay = false;
        this.$emit('showOverlay', false);
    }

    async updateList(list: FavoriteListViewObjectExtended) {
        if (list.loading || !this.isLoggedIn) {
            return;
        }

        list.loading = true;

        try {
            // The user doesn't have any favourite lists yet,
            // so we need to create the default one first
            if (list.id === myAccountStore.defaultList.id) {
                const createModel: CreateFavoriteListRequest = {
                    name: list.name,
                    description: list.description,
                    shareWithOrganization: [],
                    shareWithUsers: []
                };

                const updatedFavouriteLists = await myAccountStore.createFavouriteList(createModel);

                // This is a bit optimistic, but, ideally, the user should always have 1 private list
                // after the creation request - our default list
                if (updatedFavouriteLists.private.length) {
                    list.id = updatedFavouriteLists.private[0].id;
                }
            }

            this.listInformation = this.listInformation.map(item => {
                const index = item.listIds.indexOf(list.id);

                if (list.checked && index === -1) {
                    item.listIds.push(list.id);
                } else if (index !== -1) {
                    item.listIds.splice(index, 1);
                }

                return item;
            });

            if (list.checked) {
                await myAccountStore.addFavoriteItem({
                    listId: list.id,
                    items: this.listInformation.map(({ itemId }) => ({
                        itemId,
                        quantity: 1
                    }))
                });
            } else {
                await myAccountStore.removeFavoriteItems({
                    listId: list.id,
                    itemIds: this.listInformation.map(({ itemId }) => itemId)
                });
            }
        } catch (e) {
            loggingService.error(e);
        } finally {
            list.loading = false;
        }
    }

    showLoginOverlay(): void {
        loginFormOverlayService.show('login');
    }
}
